import { useReactiveVar } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import {
    DocumentTemplateView,
    DocumentTemplateViewItem,
} from '@deltek/specification-client/core/Models/documentTemplate'
import { ProductTypePropertiesInputType } from '@deltek/specification-client/core/Models/productType'
import { ProductTypeProperty } from '@deltek/specification-client/core/Models/productTypeProperty'
import { ProjectManufacturerProductDetail } from '@deltek/specification-client/core/Models/projectManufacturerProductDetail'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import { RemoveProjectContentInput } from '@deltek/specification-client/core/Models/projectProductProperty'
import { PropertyOption } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import {
    isContentTeam,
    isOmWorkspaceUser,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { NuggetResponse } from 'api/graphQL/types/projectElement.types'
import { processEnv } from 'components/configuration/environmentVariables'
import { ExecutionResult } from 'graphql'
import React from 'react'
import { WORKSPACE_ENABLED_PTP_OPTIONS } from 'shared/utils/workspaceUtils'
import { AreaContext } from '../Common/AreaContext'
import CustomTabs from '../Common/CustomTabs'
import { TabsProvider } from '../Common/TabsContext'
import {
    NormalizedChildDocumentTemplateViewItem,
    NormalizedDocumentTemplateViewItem,
} from '../Common/model'
import {
    flattenedtTextContentElements,
    groupTemplate,
    handleManufacturerUpdate,
} from '../Common/utils'
import { HandleContentItemUpdateParams } from '../WorkspaceContentArea'

interface PropertiesAreaProps {
    nugget?: NuggetResponse
    loading: boolean
    showGlobalSnackbar?: (options?: any) => Promise<ExecutionResult<any>>
    handleContentItemUpdate: (params: HandleContentItemUpdateParams) => void
    handleDeleteProperty?: (
        inputValues: Partial<RemoveProjectContentInput>,
        isArticle?: boolean
    ) => void
    getUpdatedNugget?: (isDTVToBeRetrieved: boolean) => void
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
}

export const PropertiesArea: React.FC<PropertiesAreaProps> = ({
    nugget,
    loading,
    handleContentItemUpdate,
    handleDeleteProperty,
    getUpdatedNugget,
    contentTeamFromStudioPage,
    isDraftMode,
}) => {
    const {
        nugget: productListingsInfo,
        getPersistedState,
        persistState,
    } = React.useContext(AreaContext)
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const [localNugget, setLocalNugget] = React.useState<
        Partial<ProjectNugget>
    >()

    const {
        contentItemNodes,
        dtv,
        productTypeProperties,
        tableList,
        tabSelected,
        projectProductFamilyId,
        isAddProhibited,
        addQueueSib,
        elementIdOnEdit,
        isAddOngoing,
        isNewAddOnQueue,
    } = useReactiveVar(workspaceContents)

    const view: Partial<DocumentTemplateView> = React.useMemo(() => dtv ?? {}, [
        dtv,
    ])

    const items: Partial<DocumentTemplateViewItem>[] = React.useMemo(
        () => dtv?.items ?? [],
        [dtv]
    )

    const [normalizedTemplates, setNormalizedTemplates] = React.useState<
        NormalizedDocumentTemplateViewItem[]
    >([])

    const nuggets: Partial<ProjectNugget>[] = React.useMemo(
        () => [
            { ...localNugget },
            ...((dtv
                ? localNugget?.projectProductGroups
                : localNugget?.productGroups) ?? []),
        ],
        [localNugget, dtv]
    )

    const [productListings, setProductListings] = React.useState<
        Partial<ProjectManufacturerProductDetail>[]
    >()

    const prevProductListings = usePrevious(productListings)

    // Gather all elements within allContentItems
    const allElements = React.useMemo(
        () =>
            contentItemNodes
                ?.reduce((elements, element) => {
                    return [
                        ...elements,
                        ...flattenedtTextContentElements(
                            element.elements ?? []
                        ),
                    ]
                }, [] as Partial<TextContentElement>[])
                .filter(
                    (value, index, array) =>
                        array.findIndex((test) => test.id === value.id) ===
                        index
                ),
        [contentItemNodes]
    )

    const general = React.useMemo(
        () =>
            normalizedTemplates.length > 0
                ? normalizedTemplates.filter(
                      (eachTemplate) =>
                          eachTemplate.children.filter(
                              (eachChildren) =>
                                  eachChildren.partType === 'General'
                          ).length > 0
                  )?.[0]
                : undefined,
        [normalizedTemplates]
    )

    const products = React.useMemo(
        () =>
            normalizedTemplates.length > 0
                ? normalizedTemplates.filter(
                      (eachTemplate) =>
                          eachTemplate.children.filter(
                              (eachChildren) =>
                                  eachChildren.partType === 'Products'
                          ).length > 0
                  )?.[0]
                : undefined,
        [normalizedTemplates]
    )

    const execution = React.useMemo(
        () =>
            normalizedTemplates.length > 0
                ? normalizedTemplates.filter(
                      (eachTemplate) =>
                          eachTemplate.children.filter(
                              (eachChildren) =>
                                  eachChildren.partType === 'Execution'
                          ).length > 0
                  )?.[0]
                : undefined,
        [normalizedTemplates]
    )

    const handleUpdateTemplateContentItems = React.useCallback(
        (contentItems: ContentItem[]) => {
            if (contentItems.length === 0) return
            handleContentItemUpdate({ updatedContentItems: contentItems })
        },
        [handleContentItemUpdate]
    )

    const handleUpdateTemplate = React.useCallback(
        (
            template: NormalizedDocumentTemplateViewItem,
            retrieveChanges?: boolean
        ) => {
            handleContentItemUpdate({
                updatedContentItems: [template.contentItem ?? {}],
                retrieveTrackedChanges: retrieveChanges,
            })
        },
        [handleContentItemUpdate]
    )

    // Based on the template, create a parent child object
    // Assume that null parentIds are the grandparents
    React.useEffect(() => {
        if (!loading) {
            const newTemplateItems = groupTemplate(
                view,
                items,
                null,
                contentItemNodes ?? [],
                allElements ?? [],
                nuggets,
                0,
                false, // excludeProductType
                '',
                undefined,
                undefined,
                productTypeProperties,
                productListings,
                undefined,
                isOMUser,
                isOMUser && contentTeamFromStudioPage,
                isContentTeamUser && contentTeamFromStudioPage,
                tableList,
                undefined,
                processEnv.REACT_APP_HideGroupSustainabilityContentInSMM !== '1' // temporary flag
            )

            setNormalizedTemplates(newTemplateItems)
        }
    }, [
        allElements,
        nuggets,
        view,
        items,
        productListings,
        localNugget,
        productTypeProperties,
        loading,
        contentItemNodes,
        isOMUser,
        isContentTeamUser,
        contentTeamFromStudioPage,
        tableList,
        processEnv,
    ])

    const updateManufacturerParagraph = React.useCallback(
        async (
            manufacturerTemplate: NormalizedDocumentTemplateViewItem,
            isForCustomManufacturer?: boolean
        ) => {
            const selectedManufacturerCount =
                productListings?.filter((listing) => listing?.isSelected)
                    ?.length ?? 0
            let actualCount = selectedManufacturerCount
            if (
                productListingsInfo?.customManufacturerProducts?.length! > 0 ||
                isForCustomManufacturer
            ) {
                actualCount += 1
            }
            const updatedManufacturerParagraph = await handleManufacturerUpdate(
                actualCount,
                manufacturerTemplate?.contentItem?.elements?.find(
                    (element) => element.type === 'OptionGroup'
                ) as Partial<TextContentElement>
            )

            const manufacturerIndex = manufacturerTemplate?.contentItem?.elements?.findIndex(
                (element) => element.type === 'OptionGroup'
            )

            handleUpdateTemplate?.({
                ...manufacturerTemplate,
                contentItem: {
                    ...manufacturerTemplate?.contentItem,
                    elements: manufacturerTemplate?.contentItem?.elements
                        ?.slice(0, manufacturerIndex)
                        .concat(updatedManufacturerParagraph)
                        .concat(
                            manufacturerTemplate.contentItem?.elements?.slice(
                                manufacturerIndex! + 1
                            )!
                        ),
                },
            })
        },
        [productListings, productListingsInfo, handleUpdateTemplate]
    )

    React.useEffect(() => {
        const previousProductListings = (prevProductListings as unknown) as Partial<
            ProjectManufacturerProductDetail
        >[]
        if (productListings && productListings !== previousProductListings) {
            const manufacturerTab = normalizedTemplates.find((template) =>
                template?.children?.find((content) =>
                    content?.children?.some((child) => child.isManufacturers)
                )
            )

            const manufacturerArticle = manufacturerTab?.children?.find(
                (child) => child?.children?.find((x) => x.isManufacturers)
            ) as NormalizedDocumentTemplateViewItem

            const manufacturerTemplate = manufacturerArticle?.children?.find(
                (child) => child.isManufacturers
            ) as NormalizedDocumentTemplateViewItem

            if (manufacturerTemplate) {
                updateManufacturerParagraph(manufacturerTemplate, false)
            }
        }
    }, [
        productListings,
        prevProductListings,
        normalizedTemplates,
        updateManufacturerParagraph,
    ])

    React.useEffect(() => {
        setProductListings(
            productListingsInfo?.productListings as ProjectManufacturerProductDetail[]
        )
    }, [productListingsInfo])

    React.useEffect(() => {
        if (!loading) {
            setLocalNugget(nugget)
        }
    }, [loading, nugget])

    /*
     * This is an upscaled version of the old persisOptionChanges
     * in ContentItemElementText, in that it processes the entire properties
     * list instead of doing it per element.
     */
    const persistOptionChanges = React.useCallback(() => {
        const getFieldStateAsOptionText = (
            fieldState: Partial<TextContentElement>
        ) => {
            let optionText: string = ''
            fieldState.elements?.forEach((element) => {
                if (element.type === 'UnitsOfMeasure') {
                    if (
                        element.imperialUnits &&
                        element.imperialUnits !== '' &&
                        element.metricUnits &&
                        element.metricUnits !== ''
                    ) {
                        optionText = optionText
                            ? `${optionText} ${element.imperialUnits} (${element.metricUnits})`
                            : `${element.imperialUnits} (${element.metricUnits})`
                    } else if (element.imperialUnits !== '') {
                        optionText = optionText
                            ? `${optionText} ${element.imperialUnits}`
                            : `${element.imperialUnits}`
                    } else {
                        optionText = optionText
                            ? `${optionText} ${element.metricUnits}`
                            : `${element.metricUnits}`
                    }
                } else {
                    optionText = `${optionText}${element.text}`
                }
            })

            return optionText
        }

        const currentPropertyOptions: ProductTypePropertiesInputType[] = getPersistedState?.(
            'propertyOption'
        )
        const newPropertyOptions: ProductTypePropertiesInputType[] =
            getPersistedState?.('propertyOption') ?? []

        type PtpElementsLookup = Record<string, Partial<TextContentElement>[]>

        const getFlattenedListOfPtpInfo = (
            ptpArray: NormalizedChildDocumentTemplateViewItem[]
        ): PtpElementsLookup => {
            return ptpArray.reduce(
                (
                    acc: PtpElementsLookup,
                    curr: NormalizedChildDocumentTemplateViewItem
                ) => {
                    const templateId =
                        curr.productTypePropertyId ??
                        curr.templateViewItemId ??
                        ''
                    if (templateId && !curr.isHidden) {
                        return {
                            ...acc,
                            [templateId]: curr.contentItem?.elements ?? [],
                            ...getFlattenedListOfPtpInfo(curr.children),
                        }
                    }

                    return {
                        ...acc,
                        ...getFlattenedListOfPtpInfo(curr.children),
                    }
                },
                {}
            )
        }

        const isUnitOfMeasure = (element: Partial<TextContentElement>) => {
            return element.imperialUnits !== '' && element.metricUnits !== ''
        }

        const templateInfo = getFlattenedListOfPtpInfo(products?.children ?? [])

        const validProperties: Partial<
            ProductTypeProperty
        >[] = productTypeProperties
            ?.filter(
                (ptp) =>
                    !!templateInfo[
                        ptp.projectElementMetadata?.baseElementId ?? ''
                    ]
            )
            ?.map((ptpDetails: Partial<ProductTypeProperty>) => {
                return {
                    id: ptpDetails.id,
                    name: ptpDetails?.name,
                    options: ptpDetails?.optionGroups?.reduce(
                        (prev: any, current: any) => {
                            return prev.concat(
                                current?.options
                                    ?.filter(
                                        (option: Partial<PropertyOption>) =>
                                            !option.isPremiumPlus
                                    )
                                    ?.map(
                                        (
                                            ptpOption: Partial<PropertyOption>
                                        ) => {
                                            return {
                                                id: ptpOption?.id,
                                                value: ptpOption?.value,
                                            }
                                        }
                                    )
                            )
                        },
                        [] as PropertyOption[]
                    ),
                    projectElementMetadata: ptpDetails?.projectElementMetadata,
                }
            })

        validProperties?.forEach((ptpItem) => {
            let actualFieldStateText: string = ''
            let actualFieldStateId: string = ''
            if (ptpItem) {
                const sourceTemplateElements =
                    templateInfo[
                        ptpItem.projectElementMetadata?.baseElementId ?? ''
                    ]

                sourceTemplateElements.forEach((fieldState) => {
                    if (fieldState.type === 'OptionGroup') {
                        const optionGroupElements = fieldState.elements
                        optionGroupElements?.forEach((optionGroupElement) => {
                            const propertyOption = ptpItem.options?.find(
                                (option) => {
                                    actualFieldStateText = ''
                                    actualFieldStateId = ''
                                    let isValidOption =
                                        option.value.normalize('NFKC') ===
                                        optionGroupElement.text?.normalize(
                                            'NFKC'
                                        )

                                    if (!isValidOption) {
                                        if (
                                            optionGroupElement.elements?.some(
                                                isUnitOfMeasure
                                            )
                                        ) {
                                            actualFieldStateText = getFieldStateAsOptionText(
                                                optionGroupElement
                                            )
                                                .toString()
                                                .normalize('NFKC')
                                            actualFieldStateId = option.id as string
                                            isValidOption =
                                                option.value.normalize(
                                                    'NFKC'
                                                ) === actualFieldStateText
                                        }
                                    } else {
                                        actualFieldStateText = optionGroupElement.text as string
                                        actualFieldStateId = option.id as string
                                    }
                                    return isValidOption
                                }
                            )

                            if (
                                optionGroupElement.optionItemState === 'OptedIn'
                            ) {
                                if (propertyOption) {
                                    const optionToAdd: ProductTypePropertiesInputType = {
                                        propertyName: ptpItem.name!,
                                        propertyOptionIds: [actualFieldStateId],
                                    }
                                    if (newPropertyOptions.length >= 0) {
                                        const property = newPropertyOptions.find(
                                            (option) =>
                                                option.propertyName ===
                                                optionToAdd.propertyName
                                        )
                                        if (property) {
                                            property.propertyOptionIds.push(
                                                actualFieldStateId
                                            )
                                            property.propertyOptionIds = property.propertyOptionIds.filter(
                                                (value, index, array) => {
                                                    return (
                                                        array.indexOf(value) ===
                                                        index
                                                    )
                                                }
                                            )
                                        } else {
                                            newPropertyOptions.push(optionToAdd)
                                        }
                                    }

                                    persistState?.(
                                        'propertyOption',
                                        newPropertyOptions
                                    )
                                }
                            } else {
                                if (
                                    newPropertyOptions &&
                                    newPropertyOptions.length > 0
                                ) {
                                    const optionIndex = newPropertyOptions.findIndex(
                                        (option: any) =>
                                            option.propertyOptionIds.includes(
                                                actualFieldStateId
                                            ) &&
                                            option.propertyName ===
                                                ptpItem.name!
                                    )
                                    if (optionIndex > -1) {
                                        if (
                                            newPropertyOptions[optionIndex]
                                                .propertyOptionIds.length === 1
                                        ) {
                                            newPropertyOptions.splice(
                                                optionIndex,
                                                1
                                            )
                                            persistState?.(
                                                'propertyOption',
                                                newPropertyOptions
                                            )
                                        } else {
                                            const index = newPropertyOptions[
                                                optionIndex
                                            ].propertyOptionIds.findIndex(
                                                (option) =>
                                                    option ===
                                                    actualFieldStateId
                                            )
                                            if (index > -1) {
                                                newPropertyOptions[
                                                    optionIndex
                                                ].propertyOptionIds.splice(
                                                    index,
                                                    1
                                                )
                                                persistState?.(
                                                    'propertyOption',
                                                    newPropertyOptions
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        })
                    }
                })
            }
        })

        const isProjectProductListingDialog = getPersistedState?.(
            'projectProductDialogOpened'
        )
        if (
            isProjectProductListingDialog &&
            JSON.stringify(currentPropertyOptions) !==
                JSON.stringify(newPropertyOptions)
        ) {
            const serviceWorker = navigator.serviceWorker
            serviceWorker?.controller?.postMessage({
                [WORKSPACE_ENABLED_PTP_OPTIONS]: newPropertyOptions,
            })
        }
    }, [getPersistedState, productTypeProperties, products, persistState])

    React.useEffect(() => {
        persistOptionChanges()
    }, [persistOptionChanges, normalizedTemplates])

    const baseNotes = React.useMemo(
        () =>
            normalizedTemplates.filter(
                (each) => each.partType === 'BaseSpecifierNote'
            ),
        [normalizedTemplates]
    )

    return (
        <TabsProvider
            haveProducts={!!products}
            handleUpdateTemplate={handleUpdateTemplate}
            handleUpdateTemplateContentItems={handleUpdateTemplateContentItems}
            removeProperty={handleDeleteProperty}
            getUpdatedNugget={getUpdatedNugget}
        >
            <CustomTabs
                baseNotes={baseNotes}
                general={general}
                products={products}
                execution={execution}
                loading={contentTeamFromStudioPage ? false : loading}
                contentTeamFromStudioPage={contentTeamFromStudioPage}
                isDraftMode={isDraftMode}
                documentTemplateView={dtv}
                tab={tabSelected}
                projectProductFamilyId={projectProductFamilyId}
                isAddProhibited={isAddProhibited}
                dtv={dtv}
                addQueueSib={addQueueSib}
                contentItemNodes={contentItemNodes}
                elementIdOnEdit={elementIdOnEdit}
                isAddOngoing={isAddOngoing}
                isNewAddOnQueue={isNewAddOnQueue}
            />
        </TabsProvider>
    )
}

export default React.memo(PropertiesArea)
